import React, { useState, useEffect, useMemo } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  TextField,
  Grid,
  Chip,
  InputAdornment,
  TablePagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress
} from '@mui/material';
import { 
  Search as SearchIcon, 
  FileDownload as DownloadIcon, 
  AssignmentTurnedIn as PassIcon,
  Cancel as FailIcon,
  FactCheck as RegistryIcon,
  Print as PrintIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Assessment as AssessmentIcon,
  MenuBook as MenuBookIcon,
  Quiz as QuizIcon,
  Assignment as WorkloadIcon
} from '@mui/icons-material';
import api from '../services/api';
import { Dialog, DialogTitle, DialogContent, DialogActions, Alert, Divider } from '@mui/material';
import { OfficialEvaluationReportPrint } from '../components/OfficialEvaluationReportPrint';
import { DEFAULT_STUDENT_MAJORS, fetchStudentMajors } from '../constants/majors';

const RegistryReport = () => {
  const [data, setData] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('ALL');
  const [selectedYear, setSelectedYear] = useState('ALL');
  const [selectedCourse, setSelectedCourse] = useState('ALL');
  const [selectedMajor, setSelectedMajor] = useState('ALL');
  const [dbMajors, setDbMajors] = useState<string[]>(DEFAULT_STUDENT_MAJORS);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Detail Dialog State
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const allMajors = useMemo(() => {
    const set = new Set<string>(dbMajors);
    data.forEach((r: any) => {
      if (r.major && r.major.trim()) set.add(r.major.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'th'));
  }, [data, dbMajors]);

  const fetchReport = async (year?: string, sem?: string, course?: string, major?: string) => {
    try {
      setLoading(true);
      const params: any = {};
      const yr = year !== undefined ? year : selectedYear;
      const sm = sem !== undefined ? sem : selectedSemester;
      const crs = course !== undefined ? course : selectedCourse;
      const mjr = major !== undefined ? major : selectedMajor;

      if (yr && yr !== 'ALL') params.academic_year = yr;
      if (sm && sm !== 'ALL') params.semester = sm;
      if (crs && crs !== 'ALL') params.course_id = crs;
      if (mjr && mjr !== 'ALL') params.major = mjr;

      const response = await api.get('/evaluations/registry-report', { params });
      setData(response.data || []);
    } catch (err) {
      console.error('Failed to fetch registry report:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial load: Fetch system settings and courses, default to current semester & academic year
  useEffect(() => {
    const initPage = async () => {
      try {
        setLoading(true);
        const [cRes, sRes, majorsRes] = await Promise.all([
          api.get('/courses'),
          api.get('/settings'),
          fetchStudentMajors()
        ]);
        setCourses(cRes.data || []);
        if (Array.isArray(majorsRes) && majorsRes.length > 0) {
          setDbMajors(majorsRes);
        }
        const settings = sRes.data || {};
        const defYear = settings.current_academic_year ? String(settings.current_academic_year) : '2567';
        const defSem = settings.current_semester ? String(settings.current_semester) : '1';
        setSelectedYear(defYear);
        setSelectedSemester(defSem);
        await fetchReport(defYear, defSem, 'ALL', 'ALL');
      } catch (err) {
        console.error('Failed to initialize registry report:', err);
      } finally {
        setInitialized(true);
        setLoading(false);
      }
    };
    initPage();
  }, []);

  // Fetch report when filter selections change after initial load
  useEffect(() => {
    if (initialized) {
      fetchReport(selectedYear, selectedSemester, selectedCourse, selectedMajor);
    }
  }, [selectedYear, selectedSemester, selectedCourse, selectedMajor]);

  const handleOpenDetail = async (placementId: string) => {
    setLoadingDetail(true);
    try {
      const res = await api.get(`/evaluations/detailed-report/${placementId}`);
      setSelectedReport(res.data);
      setOpenDetail(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handlePrint = () => {
      window.print();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredData = data.filter((r: any) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchSearch = 
        (r.first_name && r.first_name.toLowerCase().includes(term)) ||
        (r.last_name && r.last_name.toLowerCase().includes(term)) ||
        (r.student_code && r.student_code.toLowerCase().includes(term)) ||
        (r.major && r.major.toLowerCase().includes(term)) ||
        (r.school_name && r.school_name.toLowerCase().includes(term));
      if (!matchSearch) return false;
    }
    return true;
  });

  // Reset page when filtering
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedYear, selectedSemester, selectedCourse, selectedMajor]);

  const handleExport = () => {
    const headers = ['รหัสนักศึกษา', 'ชื่อ-นามสกุล', 'สาขาวิชา', 'โรงเรียน', 'วิชา', 'ภาคเรียน/ปีการศึกษา', 'คะแนนด้านวิชาชีพ', 'คะแนนด้านวิชาเอก', 'คะแนนครูพี่เลี้ยง', 'คะแนนสมรรถนะเฉลี่ย (%)', 'คะแนนภาระงาน', 'สถานะการฝึกสอน', 'หมายเหตุ'];
    const rows = filteredData.map((r: any) => {
        return [
            r.student_code,
            `"${r.prefix || ''}${r.first_name} ${r.last_name}"`,
            `"${r.major}"`,
            `"${r.school_name}"`,
            `"${r.course_code ? `${r.course_code} ${r.course_name || ''}` : '-'}"`,
            `"${r.semester || '-'}/${r.academic_year || '-'}"`,
            r.requires_supervision === 0 ? 'ยกเว้น' : (r.prof_score || '-'),
            r.requires_supervision === 0 ? 'ยกเว้น' : (r.spec_score || '-'),
            r.requires_supervision === 0 ? 'ยกเว้น' : (r.co_score || '-'),
            r.requires_supervision === 0 ? 'ยกเว้นการนิเทศ' : (r.average_score || '0.00'),
            r.workload_score ? `${r.workload_score} / ${r.workload_max_score || 0}` : '-',
            r.status === 'PASSED' ? 'ผ่านเกณฑ์ (PASS)' : (r.status === 'FAILED' ? 'ไม่ผ่านเกณฑ์ (FAIL)' : 'รอประเมินครบ'),
            `"${r.remark || '-'}"`
        ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map((row: any) => row.join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `tpms_registry_summary_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: 2, 
        mb: 3 
      }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '1.1rem', sm: '1.5rem' }, fontWeight: 700 }}>
          <RegistryIcon sx={{ mr: 1, color: 'primary.main' }} /> รายงานฝ่ายทะเบียน (Registry Report)
        </Typography>
        <Button 
          variant="contained" 
          color="success" 
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
        >
          ส่งออกรายงาน (CSV)
        </Button>
      </Box>

      {/* Filter Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          {/* Semester */}
          <Grid size={{ xs: 6, sm: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="reg-sem-label">ภาคเรียน</InputLabel>
              <Select
                labelId="reg-sem-label"
                value={selectedSemester}
                label="ภาคเรียน"
                onChange={(e) => {
                  setSelectedSemester(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">ทั้งหมด</MenuItem>
                <MenuItem value="1">ภาคเรียนที่ 1</MenuItem>
                <MenuItem value="2">ภาคเรียนที่ 2</MenuItem>
                <MenuItem value="3">ภาคเรียนที่ 3</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Academic Year */}
          <Grid size={{ xs: 6, sm: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="reg-year-label">ปีการศึกษา</InputLabel>
              <Select
                labelId="reg-year-label"
                value={selectedYear}
                label="ปีการศึกษา"
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">ทั้งหมด</MenuItem>
                {['2569', '2568', '2567', '2566', '2565', '2564'].map(yr => (
                  <MenuItem key={yr} value={yr}>{yr}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Course */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="reg-course-label">รายวิชาฝึกประสบการณ์</InputLabel>
              <Select
                labelId="reg-course-label"
                value={selectedCourse}
                label="รายวิชาฝึกประสบการณ์"
                renderValue={(selected) => {
                  if (selected === 'ALL') return 'ทุกรายวิชา (ทั้งหมด)';
                  const found = courses.find((c: any) => c.id === selected);
                  return found ? `${found.course_code} ${found.course_name}` : selected;
                }}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">
                  <em>ทุกรายวิชา (ทั้งหมด)</em>
                </MenuItem>
                {courses.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.course_code} {c.course_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Major */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="reg-major-label">สาขาวิชา</InputLabel>
              <Select
                labelId="reg-major-label"
                value={selectedMajor}
                label="สาขาวิชา"
                onChange={(e) => {
                  setSelectedMajor(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">
                  <em>ทุกสาขาวิชา (ทั้งหมด)</em>
                </MenuItem>
                {allMajors.map((m: string) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Search Bar */}
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="ค้นหารหัสนักศึกษา, ชื่อ-นามสกุล, หรือโรงเรียน..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: 2 }}>
        {loading && <LinearProgress />}
        <Table sx={{ minWidth: 1100 }}>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>นักศึกษา</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>โรงเรียน / รายวิชา</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>ด้านวิชาชีพครู</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>ด้านวิชาเอก</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>ครูพี่เลี้ยง</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>เฉลี่ยสมรรถนะ</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f0fdf4' }}>คะแนนภาระงาน</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>สถานะสำเร็จวิชา</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>หมายเหตุ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r: any) => (
              <TableRow key={r.student_code} hover onClick={() => handleOpenDetail(r.placement_id)} sx={{ cursor: 'pointer' }}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{r.prefix || ''}{r.first_name} {r.last_name}</Typography>
                  <Typography variant="caption" color="textSecondary">{r.student_code} | {r.major}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{r.school_name}</Typography>
                  {r.course_code && (
                    <Typography variant="caption" color="primary" sx={{ display: 'block', fontWeight: 600 }}>
                      {r.course_code} {r.course_name}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">{r.requires_supervision === 0 ? <Typography variant="caption" color="textSecondary">ยกเว้น</Typography> : (r.prof_score ? `${r.prof_score}%` : '-')}</TableCell>
                <TableCell align="center">{r.requires_supervision === 0 ? <Typography variant="caption" color="textSecondary">ยกเว้น</Typography> : (r.spec_score ? `${r.spec_score}%` : '-')}</TableCell>
                <TableCell align="center">{r.requires_supervision === 0 ? <Typography variant="caption" color="textSecondary">ยกเว้น</Typography> : (r.co_score ? `${r.co_score}%` : '-')}</TableCell>
                <TableCell align="center">
                  {r.requires_supervision === 0 ? (
                    <Chip label="ยกเว้นการนิเทศ" size="small" variant="outlined" sx={{ fontSize: '0.72rem', color: 'text.secondary' }} />
                  ) : (
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                      {r.average_score ? `${parseFloat(r.average_score).toFixed(1)}%` : '-'}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ bgcolor: '#f0fdf4' }}>
                  {r.workload_score ? (
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                      {r.workload_score} / {r.workload_max_score || 0}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="textSecondary">-</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {r.status === 'PASSED' ? (
                    <Chip icon={<PassIcon />} label="ผ่านเกณฑ์" color="success" size="small" />
                  ) : r.status === 'FAILED' ? (
                    <Chip icon={<FailIcon />} label="ไม่ผ่านเกณฑ์" color="error" size="small" variant="outlined" />
                  ) : (
                    <Chip label="รอประเมินครบ" size="small" variant="outlined" sx={{ fontWeight: 'bold', color: 'text.secondary' }} />
                  )}
                </TableCell>
                <TableCell align="center">
                  {r.remark ? (
                    <Chip 
                      label={r.remark} 
                      size="small" 
                      variant="outlined"
                      sx={{ 
                        fontSize: '0.72rem', 
                        borderColor: r.remark.includes('ไม่ผ่าน') ? '#ef5350' : '#ff9800',
                        color: r.remark.includes('ไม่ผ่าน') ? '#c62828' : '#e65100',
                        bgcolor: r.remark.includes('ไม่ผ่าน') ? '#ffebee' : '#fff8e1',
                        fontWeight: 600
                      }} 
                    />
                  ) : (
                    <Typography variant="caption" color="textSecondary">-</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filteredData.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>ไม่พบข้อมูลตามเงื่อนไข</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="รายการต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
        />
      </TableContainer>

      {/* Detail Dialog (Individual Report) */}
      <Dialog 
        open={openDetail} 
        onClose={() => setOpenDetail(false)} 
        fullWidth 
        maxWidth="lg"
        slotProps={{
          paper: {
            sx: { borderRadius: 4, overflow: 'hidden' }
          }
        }}
      >
        <DialogTitle className="no-print" sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: '#f8fafc',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          px: 3,
          py: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
            รายละเอียดผลการประเมินรายบุคคล
          </Typography>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<PrintIcon />} 
            onClick={handlePrint} 
            className="no-print"
            sx={{ borderRadius: 2, fontWeight: 'bold', px: 2 }}
          >
            พิมพ์รายงาน
          </Button>
        </DialogTitle>
        
        <DialogContent dividers sx={{ px: 4, py: 3 }}>
          {loadingDetail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ mt: 2 }} color="textSecondary">กำลังดึงคะแนนเชิงลึก...</Typography>
            </Box>
          ) : selectedReport ? (
            <Box id="printable-report">
              <Box className="screen-report-view">
                {(() => {
                  const requiresSupervision = selectedReport.student?.requires_supervision !== false && Number(selectedReport.student?.requires_supervision) !== 0;

                return (
                  <>
                    <Typography variant="h5" align="center" sx={{ fontWeight: 800, mb: 3, color: 'primary.main', fontSize: { xs: '1.25rem', md: '1.65rem' } }}>
                      {requiresSupervision ? 'รายงานสรุปผลการประเมินสมรรถนะและภาระงานนักศึกษา' : 'รายงานสรุปผลการประเมินภาระงานนักศึกษา'}
                    </Typography>
                    
                    {!requiresSupervision && (
                      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                        <b>กลุ่มวิชาที่ไม่ต้องรับการนิเทศ:</b> รายวิชานี้ ({selectedReport.student?.course_code || ''} {selectedReport.student?.course_name || ''}) ได้รับการกำหนดให้<b>ยกเว้นการนิเทศ</b> (ประเมินเฉพาะคะแนนภาระงานและผลการสอบประมวลความรู้ตามที่หลักสูตรกำหนด)
                      </Alert>
                    )}

                    {/* Student Info Card */}
                    <Paper id="printable-report-info-card" sx={{ p: 2, px: 3, borderRadius: 3, bgcolor: '#fafafa', mb: 3, border: '1px solid rgba(0,0,0,0.03)' }}>
                      <Grid container spacing={2}>
                        {/* Left Column: ข้อมูลส่วนบุคคล */}
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <PersonIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              <b>ชื่อ-นามสกุลนักศึกษา:</b> {selectedReport.student.prefix || ''}{selectedReport.student.first_name} {selectedReport.student.last_name}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', pl: 4 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              <b>รหัสนักศึกษา:</b> {selectedReport.student.student_code}
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Right Column: ข้อมูลการฝึกปฏิบัติงาน */}
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <SchoolIcon sx={{ color: 'success.main', mr: 1 }} />
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              <b>โปรแกรมวิชา (สาขาวิชา):</b> {selectedReport.student.major}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', pl: 4 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              <b>โรงเรียนฝึกประสบการณ์:</b> {selectedReport.student.school_name}
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Course Info */}
                        <Grid size={{ xs: 12 }}>
                          <Divider sx={{ my: 0.5 }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <MenuBookIcon sx={{ color: 'info.main', mr: 1, fontSize: 20 }} />
                            <Typography variant="body2">
                              <b>รายวิชาฝึกประสบการณ์:</b> {selectedReport.student.course_code ? `${selectedReport.student.course_code} - ${selectedReport.student.course_name}` : 'ไม่ระบุรายวิชา'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* SECTION A: Competency Evaluation (Only for courses requiring supervision) */}
                    {requiresSupervision && (
                      <Box sx={{ mb: 4 }}>
                        {/* Committee Names Section */}
                        <Typography variant="subtitle1" className="committee-section-title" sx={{ fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                          <GroupIcon sx={{ mr: 1, color: 'text.secondary' }} /> คณะกรรมการประเมิน
                        </Typography>
                        <Grid container spacing={2} className="committee-cards-container" sx={{ mb: 4 }}>
                          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                            <Paper className="committee-card" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, bgcolor: '#fcfdfe', border: '1px solid rgba(25, 118, 210, 0.06)' }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>อาจารย์นิเทศก์ (วิชาชีพ):</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', mt: 0.5 }}>
                                {selectedReport.evaluators.prof || 'ยังไม่มีการประเมิน'}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                            <Paper className="committee-card" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, bgcolor: '#fffdfc', border: '1px solid rgba(156, 39, 176, 0.06)' }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>อาจารย์นิเทศก์ (วิชาเอก):</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#9c27b0', mt: 0.5 }}>
                                {selectedReport.evaluators.spec1 || 'ยังไม่มีการประเมิน'}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                            <Paper className="committee-card" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, bgcolor: '#fcfdfb', border: '1px solid rgba(46, 125, 50, 0.06)' }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>ครูพี่เลี้ยง:</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main', mt: 0.5 }}>
                                {selectedReport.evaluators.co || 'ยังไม่มีการประเมิน'}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                        {/* Rubric Score Matrix Table */}
                        <Typography variant="subtitle1" className="rubric-section-title" sx={{ fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                          <AssessmentIcon sx={{ mr: 1, color: 'text.secondary' }} /> คะแนนดิบแต่ละสมรรถนะ
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" className="rubric-table-container" sx={{ borderRadius: 3, mb: 4, overflow: 'hidden' }}>
                          <Table size="small" sx={{ '& td, & th': { borderRight: '1px solid rgba(224, 224, 224, 1)' } }}>
                            <TableHead>
                              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell align="center" rowSpan={2} sx={{ fontWeight: 'bold', width: '15%', bgcolor: '#f1f5f9' }}>รายการประเมิน</TableCell>
                                <TableCell align="center" colSpan={3} sx={{ fontWeight: 'bold', bgcolor: 'rgba(25, 118, 210, 0.03)' }}>สมรรถนะด้านการปฏิบัติงาน (60 คะแนน)</TableCell>
                                <TableCell align="center" colSpan={5} sx={{ fontWeight: 'bold', bgcolor: 'rgba(46, 125, 50, 0.03)' }}>สมรรถนะด้านการปฏิบัติตน (40 คะแนน)</TableCell>
                              </TableRow>
                              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell align="center" sx={{ minWidth: 70 }}><b>1.1</b><br/>ปฏิบัติหน้าที่ครู<br/>(20)</TableCell>
                                <TableCell align="center" sx={{ minWidth: 70 }}><b>1.2</b><br/>การจัดการเรียนรู้<br/>(24)</TableCell>
                                <TableCell align="center" sx={{ minWidth: 70 }}><b>1.3</b><br/>ความสัมพันธ์ชุมชน<br/>(16)</TableCell>
                                <TableCell align="center" sx={{ minWidth: 70 }}><b>2.1</b><br/>จรรยาบรรณวิชาชีพ<br/>(8)</TableCell>
                                <TableCell align="center" sx={{ minWidth: 70 }}><b>2.2</b><br/>จรรยาบรรณตนเอง<br/>(8)</TableCell>
                                <TableCell align="center" sx={{ minWidth: 70 }}><b>2.3</b><br/>จรรยาบรรณผู้รับบริการ<br/>(8)</TableCell>
                                <TableCell align="center" sx={{ minWidth: 70 }}><b>2.4</b><br/>จรรยาบรรณเพื่อนร่วมงาน<br/>(8)</TableCell>
                                <TableCell align="center" sx={{ minWidth: 70 }}><b>2.5</b><br/>จรรยาบรรณสังคม<br/>(8)</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>คะแนนเฉลี่ย</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['1.1'] !== undefined ? selectedReport.sections['1.1'].toFixed(2) : '-'}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['1.2'] !== undefined ? selectedReport.sections['1.2'].toFixed(2) : '-'}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['1.3'] !== undefined ? selectedReport.sections['1.3'].toFixed(2) : '-'}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['2.1'] !== undefined ? selectedReport.sections['2.1'].toFixed(2) : '-'}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['2.2'] !== undefined ? selectedReport.sections['2.2'].toFixed(2) : '-'}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['2.3'] !== undefined ? selectedReport.sections['2.3'].toFixed(2) : '-'}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['2.4'] !== undefined ? selectedReport.sections['2.4'].toFixed(2) : '-'}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>{selectedReport.sections['2.5'] !== undefined ? selectedReport.sections['2.5'].toFixed(2) : '-'}</TableCell>
                              </TableRow>
                              <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>คะแนนแยกส่วน</TableCell>
                                <TableCell align="center" colSpan={3} sx={{ fontWeight: 700, color: 'primary.dark', fontSize: '1rem' }}>
                                  {selectedReport.totals['1'] !== undefined ? `${selectedReport.totals['1'].toFixed(2)} / 60` : '-'}
                                </TableCell>
                                <TableCell align="center" colSpan={5} sx={{ fontWeight: 700, color: 'success.dark', fontSize: '1rem' }}>
                                  {selectedReport.totals['2'] !== undefined ? `${selectedReport.totals['2'].toFixed(2)} / 40` : '-'}
                                </TableCell>
                              </TableRow>
                              <TableRow sx={{ bgcolor: 'rgba(25, 118, 210, 0.06)' }}>
                                <TableCell colSpan={4} sx={{ fontWeight: 800, textAlign: 'right', py: 1.5 }}>คะแนนรวมสุทธิ (100 คะแนนเต็ม)</TableCell>
                                <TableCell colSpan={5} align="center" sx={{ fontWeight: 900, fontSize: '1.4rem', color: 'primary.main', py: 1.5 }}>
                                  {selectedReport.totals['grand'] !== undefined ? `${selectedReport.totals['grand'].toFixed(2)}%` : '-'}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {/* Competency Status Summary Banner */}
                        <Box className="status-summary-banner" sx={{ 
                          mb: 4, 
                          p: 2.5, 
                          borderRadius: 3, 
                          textAlign: 'center',
                          background: selectedReport.roundCount === 3 
                            ? (selectedReport.totals['grand'] >= 60 ? 'linear-gradient(to right, #e8f5e9, #c8e6c9)' : 'linear-gradient(to right, #ffebee, #ffcdd2)') 
                            : 'linear-gradient(to right, #fff3e0, #ffe0b2)',
                          color: selectedReport.roundCount === 3 
                            ? (selectedReport.totals['grand'] >= 60 ? '#1b5e20' : '#b71c1c') 
                            : '#e65100',
                          border: '1px solid rgba(0,0,0,0.03)'
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            ผลการประเมินสมรรถนะสรุป: {
                              selectedReport.roundCount === 3 
                                ? (selectedReport.totals['grand'] >= 60 ? 'ผ่านเกณฑ์สมรรถนะ (PASSED)' : 'ไม่ผ่านเกณฑ์สมรรถนะ (FAILED)') 
                                : `รอดำเนินการประเมิน (PENDING) - ได้รับผลประเมินแล้ว ${selectedReport.roundCount} / 3 ส่วน`
                            }
                          </Typography>
                          {selectedReport.roundCount === 3 && (
                            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                              คะแนนเฉลี่ยรวม 3 ฝ่ายสุทธิคิดเป็น {selectedReport.totals['grand']?.toFixed(2)}% (เกณฑ์ผ่านขั้นต่ำของคณะคือ 60%)
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* SECTION B: Workload Evaluation Score (ผลคะแนนประเมินภาระงาน) */}
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="subtitle1" className="workload-section-title" sx={{ fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                        <WorkloadIcon sx={{ mr: 1, color: 'primary.main' }} /> คะแนนประเมินภาระงาน (Workload Score)
                      </Typography>

                      {selectedReport.workload ? (
                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                          <Grid container spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
                            <Grid size={{ xs: 12, sm: 7 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                {selectedReport.workload.form_title}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                อาจารย์ผู้ประเมิน: <b>{selectedReport.workload.evaluator_name || 'อาจารย์นิเทศก์ประจำสาขาวิชา'}</b>
                                {selectedReport.workload.updated_at ? ` | บันทึกล่าสุด: ${new Date(selectedReport.workload.updated_at).toLocaleString('th-TH')}` : ''}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 5 }} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>
                                รวมคะแนนภาระงานที่ได้
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: selectedReport.workload.is_passed ? 'success.main' : 'error.main' }}>
                                  {selectedReport.workload.score} / {selectedReport.workload.max_score}
                                </Typography>
                                <Chip 
                                  label={selectedReport.workload.is_passed ? `ผ่านเกณฑ์ (${selectedReport.workload.percentage.toFixed(1)}%)` : `ไม่ผ่านเกณฑ์ (${selectedReport.workload.percentage.toFixed(1)}%)`}
                                  color={selectedReport.workload.is_passed ? 'success' : 'error'}
                                  size="small"
                                  sx={{ fontWeight: 700 }}
                                />
                              </Box>
                            </Grid>
                          </Grid>

                          {/* Rubric Items Breakdown Table */}
                          {selectedReport.workload.items && selectedReport.workload.items.length > 0 && (
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                              <Table size="small">
                                <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 700, width: 50 }}>ลำดับ</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>รายการภาระงานตามเกณฑ์รูบริก</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, width: 100 }}>คะแนนเต็ม</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, width: 100 }}>คะแนนที่ได้</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {selectedReport.workload.items.map((item: any, idx: number) => (
                                    <TableRow key={item.id || idx} hover>
                                      <TableCell>{idx + 1}</TableCell>
                                      <TableCell sx={{ fontWeight: 500 }}>
                                        {item.item_code ? `${item.item_code} ` : ''}{item.item_name || item.title || `ภาระงานข้อที่ ${idx + 1}`}
                                      </TableCell>
                                      <TableCell align="center">{item.max_score}</TableCell>
                                      <TableCell align="center" sx={{ fontWeight: 700, color: item.score !== null ? 'primary.main' : 'text.disabled' }}>
                                        {item.score !== null ? item.score : '-'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </Paper>
                      ) : (
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                          ยังไม่ได้รับการบันทึกคะแนนประเมินภาระงานจากอาจารย์นิเทศก์ประจำสาขาวิชา
                        </Alert>
                      )}
                    </Box>

                    {/* SECTION C: Comprehensive Exam Score (ผลคะแนนสอบประมวลความรู้) */}
                    {(Number(selectedReport.student?.requires_exam) === 1 || selectedReport.exam) && (
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" className="exam-section-title" sx={{ fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                          <QuizIcon sx={{ mr: 1, color: '#9333ea' }} /> ผลคะแนนการสอบประมวลความรู้ (Comprehensive Exam)
                        </Typography>

                        {selectedReport.exam ? (
                          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#fdf4ff', border: '1px solid #f0abfc' }}>
                            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                              <Grid size={{ xs: 12, sm: 7 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#581c87' }}>
                                  {selectedReport.exam.exam_title || 'การสอบประมวลผลความรู้'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                  สถานะ: <b>{selectedReport.exam.status}</b> | ส่งข้อสอบเมื่อ: {selectedReport.exam.submitted_at ? new Date(selectedReport.exam.submitted_at).toLocaleString('th-TH') : '-'}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 5 }} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 600 }}>
                                  คะแนนสอบที่ได้
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
                                  <Typography variant="h4" sx={{ fontWeight: 800, color: selectedReport.exam.is_passed ? 'success.main' : 'error.main' }}>
                                    {selectedReport.exam.score} / {selectedReport.exam.total_points}
                                  </Typography>
                                  <Chip 
                                    label={selectedReport.exam.is_passed ? `ผ่านเกณฑ์ (${selectedReport.exam.percentage.toFixed(1)}%)` : `ไม่ผ่านเกณฑ์ (${selectedReport.exam.percentage.toFixed(1)}%)`}
                                    color={selectedReport.exam.is_passed ? 'success' : 'error'}
                                    size="small"
                                    sx={{ fontWeight: 700 }}
                                  />
                                </Box>
                              </Grid>
                            </Grid>
                          </Paper>
                        ) : (
                          <Alert severity="info" sx={{ borderRadius: 2 }}>
                            รายวิชานี้กำหนดให้มีการสอบประมวลความรู้ แต่นักศึกษายังไม่ได้เข้าสอบหรืออยู่ระหว่างรอส่งผลการสอบ
                          </Alert>
                        )}
                      </Box>
                    )}

                    {/* Overall Summary for non-supervision course */}
                    {!requiresSupervision && selectedReport.workload && (
                      <Box className="status-summary-banner" sx={{ 
                        mt: 2, 
                        p: 2.5, 
                        borderRadius: 3, 
                        textAlign: 'center',
                        background: selectedReport.workload.is_passed 
                          ? 'linear-gradient(to right, #e8f5e9, #c8e6c9)' 
                          : 'linear-gradient(to right, #ffebee, #ffcdd2)',
                        color: selectedReport.workload.is_passed ? '#1b5e20' : '#b71c1c',
                        border: '1px solid rgba(0,0,0,0.03)'
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          ผลการประเมินสรุป: {selectedReport.workload.is_passed ? 'ผ่านเกณฑ์ภาระงาน (PASSED)' : 'ไม่ผ่านเกณฑ์ภาระงาน (FAILED)'}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                          คะแนนรวมภาระงานคิดเป็น {selectedReport.workload.percentage.toFixed(1)}% (เกณฑ์ผ่านขั้นต่ำของคณะคือ 60%)
                        </Typography>
                      </Box>
                    )}
                  </>
                );
              })()}
              </Box>

              {/* Official Print Layout (Rendered only during print) */}
              <OfficialEvaluationReportPrint report={selectedReport} />

              <style>
                {`
                  .official-print-document {
                    display: none !important;
                  }
                  @media print {
                    @page {
                      size: A4 portrait;
                      margin: 6mm 8mm;
                    }
                    body {
                      background: #ffffff !important;
                      -webkit-print-color-adjust: exact !important;
                      print-color-adjust: exact !important;
                    }
                    #root,
                    .screen-report-view,
                    .no-print,
                    .MuiDialogTitle-root,
                    .MuiDialogActions-root {
                      display: none !important;
                    }
                    .official-print-document {
                      display: block !important;
                      width: 100% !important;
                    }
                    .MuiDialog-root {
                      position: absolute !important;
                      left: 0 !important;
                      top: 0 !important;
                      width: 100% !important;
                      height: auto !important;
                      overflow: visible !important;
                    }
                    .MuiBackdrop-root {
                      display: none !important;
                    }
                    .MuiDialog-container {
                      height: auto !important;
                      display: block !important;
                      padding: 0 !important;
                    }
                    .MuiDialog-paper {
                      box-shadow: none !important;
                      border: none !important;
                      margin: 0 !important;
                      padding: 0 !important;
                      width: 100% !important;
                      max-width: 100% !important;
                      position: absolute !important;
                      left: 0 !important;
                      top: 0 !important;
                      height: auto !important;
                      overflow: visible !important;
                      background: transparent !important;
                    }
                    .MuiDialogContent-root {
                      border: none !important;
                      padding: 0 !important;
                      overflow: visible !important;
                    }
                    #printable-report {
                      padding: 0 !important;
                      margin: 0 !important;
                      width: 100% !important;
                    }
                  }
                `}
              </style>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderTop: '1px solid rgba(0,0,0,0.05)' }} className="no-print">
          <Button 
            variant="contained" 
            onClick={() => setOpenDetail(false)}
            sx={{ px: 3, borderRadius: 2, fontWeight: 'bold' }}
          >
            ปิดหน้าต่าง
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegistryReport;
